import { useEffect } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';

import { resolveAnchorId } from './index';

const POLL_INTERVAL_MS = 100;
const MAX_WAIT_MS = 5000;

/**
 * Smooth-scroll by default (matching the nav-bar in-page navigation), but jump
 * instantly when the visitor has requested reduced motion.
 */
const getScrollBehavior = (): ScrollBehavior => {
	const prefersReducedMotion =
		getDocument()?.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
	return prefersReducedMotion ? 'auto' : 'smooth';
};

/**
 * Polls for the element with `anchorId` — the target section is lazy-loaded
 * and auth-gated, so it may not exist yet — and scrolls it into view once it
 * appears. The shell keeps the scroll container below the navbar, so anchors
 * do not need to compensate for its height.
 *
 * Returns a cleanup that cancels a pending poll.
 */
const scrollToAnchorWhenReady = (anchorId: string): (() => void) => {
	let elapsed = 0;
	let timer: ReturnType<typeof setTimeout> | undefined;

	const tryScroll = (): void => {
		const el = getDocument()?.getElementById(anchorId);
		if (el) {
			el.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
			return;
		}
		elapsed += POLL_INTERVAL_MS;
		if (elapsed < MAX_WAIT_MS) {
			timer = setTimeout(tryScroll, POLL_INTERVAL_MS);
		}
	};
	tryScroll();

	return () => {
		if (timer) {
			clearTimeout(timer);
		}
	};
};

/**
 * Scrolls the microsite to the section named by the current URL hash
 * (`#cli` / `#mcp` / `#getting-started`, case-insensitive):
 *
 *  - once on first mount (full page load / QR deep-link), and
 *  - again whenever the fragment changes in-page via `hashchange` (e.g. the
 *    user edits the `#...` in the address bar) without a full reload.
 *
 * Reads the live hash each time (so a new fragment scrolls to the new section)
 * and waits for the lazily-rendered anchor element to appear. No-ops when the
 * hash does not map to a known section.
 */
export const useScrollToHash = (): void => {
	useEffect(() => {
		let cancelPending: (() => void) | undefined;

		const scrollToCurrentHash = (): void => {
			// Cancel any in-flight poll first, so changing the hash to an
			// unknown/empty value (or changing it rapidly) never lets a stale
			// poll scroll to the previous section once its element appears.
			cancelPending?.();
			cancelPending = undefined;
			const anchorId = resolveAnchorId(getDocument()?.location.hash ?? '');
			if (!anchorId) {
				return;
			}
			cancelPending = scrollToAnchorWhenReady(anchorId);
		};

		scrollToCurrentHash();
		const unbind = bind(window, { type: 'hashchange', listener: scrollToCurrentHash });

		return () => {
			unbind();
			cancelPending?.();
		};
	}, []);
};
